import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Inline-only markdown renderer for translation strings.
 * Supports:
 *   `code`           →  <code>
 *   **bold**         →  <strong>
 *   [text](url)      →  <a> (external) or <Link> (internal, starts with /)
 *
 * Pass a string (usually the result of t('key')). Escape a delimiter with a
 * backslash (e.g. \`) to render it literally.
 */
export function Md({
  children,
  className,
  as: As = 'span',
}: {
  children: string;
  className?: string;
  as?: 'span' | 'p' | 'div';
}) {
  return <As className={className}>{renderInline(children)}</As>;
}

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let buffer = '';
  let key = 0;
  const pushBuffer = () => {
    if (buffer) {
      nodes.push(buffer);
      buffer = '';
    }
  };

  let i = 0;
  while (i < text.length) {
    const ch = text[i];

    // Escape: \` \* \[ — render next char literally.
    if (ch === '\\' && i + 1 < text.length) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }

    // Code: `...`
    if (ch === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        pushBuffer();
        nodes.push(
          <code
            key={key++}
            className="rounded bg-slate-800/80 px-1.5 py-0.5 font-mono text-[0.85em] text-cyan-300"
          >
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }

    // Bold: **...**
    if (ch === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        pushBuffer();
        nodes.push(
          <strong key={key++} className="font-semibold text-slate-100">
            {text.slice(i + 2, end)}
          </strong>,
        );
        i = end + 2;
        continue;
      }
    }

    // Link: [text](url)
    if (ch === '[') {
      const textEnd = text.indexOf(']', i + 1);
      if (textEnd !== -1 && text[textEnd + 1] === '(') {
        const urlEnd = text.indexOf(')', textEnd + 2);
        if (urlEnd !== -1) {
          pushBuffer();
          const label = text.slice(i + 1, textEnd);
          const url = text.slice(textEnd + 2, urlEnd);
          const linkClass = 'text-cyan-400 hover:text-cyan-300 underline';
          if (/^https?:/.test(url)) {
            nodes.push(
              <a
                key={key++}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                {label}
              </a>,
            );
          } else {
            nodes.push(
              <Link key={key++} to={url} className={linkClass}>
                {label}
              </Link>,
            );
          }
          i = urlEnd + 1;
          continue;
        }
      }
    }

    buffer += ch;
    i++;
  }

  pushBuffer();
  return nodes;
}
